import { Button, useDisclosure } from '@chakra-ui/react';
import { useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { VoucherInformationForm } from '../components/forms/voucher-information-form';
import { VoucherProductForm } from '../components/forms/voucher-product-form';
import { VoucherTargetForm } from '../components/forms/voucher-target-form';
import { VoucherUsageForm } from '../components/forms/voucher-usage-form';
import { VoucherFormLayout } from '../components/layouts/voucher-form-layout';
import { VoucherEditModal } from '../components/voucher-edit-modal';
import { VoucherPreview } from '../components/voucher-preview';
import { useVoucherDetail, useVoucherUpdate } from '../hooks';
import type { CreateVoucherRequest } from '../types';

const STEP_COMPONENTS: ReadonlyArray<
  (props: {
    form: UseFormReturn<CreateVoucherRequest>;
    disabled?: boolean;
  }) => JSX.Element
> = [
  ({ form, disabled }) => (
    <VoucherInformationForm form={form} disabled={disabled} />
  ),
  ({ form, disabled }) => <VoucherTargetForm form={form} disabled={disabled} />,
  ({ form, disabled }) => <VoucherUsageForm form={form} disabled={disabled} />,
  ({ form, disabled }) => (
    <VoucherProductForm form={form} disabled={disabled} />
  ),
];

const renderStep = (
  stepIndex: number,
  form: UseFormReturn<CreateVoucherRequest>,
  disabled: boolean
) => {
  const Component = STEP_COMPONENTS[stepIndex] ?? STEP_COMPONENTS[0];
  return <Component form={form} disabled={disabled} />;
};

export function VoucherDetailPage() {
  const navigate = useNavigate();
  const { form, isLoading, voucherId, voucherData } = useVoucherDetail();
  const [currentStep, setCurrentStep] = useState(0);
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();

  const { handleUpdate, isUpdating } = useVoucherUpdate({
    voucherId: voucherId || '',
    form,
    onSuccess: () => {
      onEditModalClose();
      // Optionally refetch data or show success message
      window.location.reload(); // Simple refresh for now
    },
  });

  const finalStepIndex = STEP_COMPONENTS.length - 1;

  const handleNextStep = () => {
    if (currentStep < finalStepIndex) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleBack = () => {
    navigate('/vouchers');
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!voucherId) {
    return <div>Voucher not found</div>;
  }

  return (
    <>
      <FormProvider {...form}>
        <VoucherFormLayout
          currentStep={currentStep}
          handleNext={handleNextStep}
          handleBack={currentStep === 0 ? handleBack : handlePreviousStep}
          preview={<VoucherPreview form={form} />}
          finalStepIndex={finalStepIndex}
          onStepChange={setCurrentStep}
          enableStepNavigation
          title="Voucher Detail"
          breadcrumbLinks={[
            { title: 'Beranda', link: '/' },
            { title: 'Voucher Management', link: '/vouchers' },
            { title: 'Detail Voucher', link: `/vouchers/detail/${voucherId}` },
          ]}
          headerAction={
            <Button
              colorScheme="blue"
              variant="outline"
              onClick={onEditModalOpen}
            >
              Edit
            </Button>
          }
          nextButtonText={currentStep === finalStepIndex ? 'Tutup' : 'Lanjut'}
          backButtonText={currentStep === 0 ? 'Kembali' : 'Sebelumnya'}
        >
          {renderStep(currentStep, form, true)}
        </VoucherFormLayout>
      </FormProvider>

      <VoucherEditModal
        isOpen={isEditModalOpen}
        onClose={onEditModalClose}
        form={form}
        onSubmit={handleUpdate}
        isSubmitting={isUpdating}
        quotaUsed={voucherData?.target_configuration?.quota ?? 0}
      />
    </>
  );
}
